import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import * as api from './growthAPI';

// Thunks
export const fetchGrowthStrategy = createAsyncThunk('growth/fetchStrategy', async (profileId, { rejectWithValue }) => {
  try {
    const res = await api.getStrategy(profileId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchGrowthTemplates = createAsyncThunk('growth/fetchTemplates', async (profileId, { rejectWithValue }) => {
  try {
    const res = await api.getTemplates(profileId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchGrowthPrerequisites = createAsyncThunk('growth/fetchPrereqs', async (profileId, { rejectWithValue }) => {
  try {
    const res = await api.getPrerequisites(profileId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const createGrowthFromTemplate = createAsyncThunk('growth/createFromTemplate', async ({ profileId, templateType }, { rejectWithValue }) => {
  try {
    const res = await api.createFromTemplate(profileId, templateType);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const activateGrowthStrategy = createAsyncThunk('growth/activate', async ({ profileId, strategyId }, { rejectWithValue }) => {
  try {
    await api.activateStrategy(profileId, strategyId);
    return strategyId;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const pauseGrowthStrategy = createAsyncThunk('growth/pause', async ({ profileId, strategyId }, { rejectWithValue }) => {
  try {
    await api.pauseStrategy(profileId, strategyId);
    return strategyId;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const generateGrowthPlan = createAsyncThunk('growth/generatePlan', async ({ profileId, strategyId }, { rejectWithValue }) => {
  try {
    const res = await api.generatePlan(profileId, strategyId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchGrowthActions = createAsyncThunk('growth/fetchActions', async ({ profileId, status, channel }, { rejectWithValue }) => {
  try {
    const res = await api.getActions(profileId, { status, channel });
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const approveGrowthAction = createAsyncThunk('growth/approveAction', async ({ profileId, actionId }, { rejectWithValue }) => {
  try {
    await api.approveAction(profileId, actionId);
    return actionId;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const rejectGrowthAction = createAsyncThunk('growth/rejectAction', async ({ profileId, actionId }, { rejectWithValue }) => {
  try {
    await api.rejectAction(profileId, actionId);
    return actionId;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchGrowthReports = createAsyncThunk('growth/fetchReports', async (profileId, { rejectWithValue }) => {
  try {
    const res = await api.getReports(profileId);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// Slice
const growthSlice = createSlice({
  name: 'growth',
  initialState: {
    strategy: null,
    templates: [],
    prerequisites: null,
    actions: [],
    reports: [],
    loading: false,
    actionsLoading: false,
    reportsLoading: false,
    templatesLoading: false,
    generatingPlan: false,
    error: null,
  },
  reducers: {
    clearGrowth(state) {
      state.strategy = null;
      state.actions = [];
      state.reports = [];
      state.error = null;
    },
    updateActionStatus(state, { payload: { actionId, status } }) {
      const action = state.actions.find((a) => a.Id === actionId);
      if (action) action.Status = status;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGrowthStrategy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGrowthStrategy.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.strategy = payload?.exists === false ? null : payload;
      })
      .addCase(fetchGrowthStrategy.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })

      .addCase(fetchGrowthTemplates.pending, (state) => {
        state.templatesLoading = true;
      })
      .addCase(fetchGrowthTemplates.fulfilled, (state, { payload }) => {
        state.templatesLoading = false;
        state.templates = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchGrowthTemplates.rejected, (state) => {
        state.templatesLoading = false;
      })

      .addCase(fetchGrowthPrerequisites.fulfilled, (state, { payload }) => {
        state.prerequisites = payload;
      })

      .addCase(createGrowthFromTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(createGrowthFromTemplate.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createGrowthFromTemplate.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })

      .addCase(generateGrowthPlan.pending, (state) => {
        state.generatingPlan = true;
      })
      .addCase(generateGrowthPlan.fulfilled, (state) => {
        state.generatingPlan = false;
      })
      .addCase(generateGrowthPlan.rejected, (state) => {
        state.generatingPlan = false;
      })

      .addCase(fetchGrowthActions.pending, (state) => {
        state.actionsLoading = true;
      })
      .addCase(fetchGrowthActions.fulfilled, (state, { payload }) => {
        state.actionsLoading = false;
        state.actions = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchGrowthActions.rejected, (state) => {
        state.actionsLoading = false;
      })

      .addCase(approveGrowthAction.fulfilled, (state, { payload }) => {
        const action = state.actions.find((a) => a.Id === payload);
        if (action) action.Status = 'approved';
      })
      .addCase(rejectGrowthAction.fulfilled, (state, { payload }) => {
        const action = state.actions.find((a) => a.Id === payload);
        if (action) action.Status = 'rejected';
      })

      .addCase(fetchGrowthReports.pending, (state) => {
        state.reportsLoading = true;
      })
      .addCase(fetchGrowthReports.fulfilled, (state, { payload }) => {
        state.reportsLoading = false;
        state.reports = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchGrowthReports.rejected, (state) => {
        state.reportsLoading = false;
      });
  },
});

export const { clearGrowth, updateActionStatus } = growthSlice.actions;
export default growthSlice.reducer;

// Selectors
export const selectGrowthStrategy = (state) => state.growth.strategy;
export const selectGrowthTemplates = (state) => state.growth.templates;
export const selectGrowthPrerequisites = (state) => state.growth.prerequisites;
export const selectGrowthActions = (state) => state.growth.actions;
export const selectGrowthReports = (state) => state.growth.reports;
export const selectGrowthLoading = (state) => state.growth.loading;
export const selectGrowthActionsLoading = (state) => state.growth.actionsLoading;
export const selectGrowthReportsLoading = (state) => state.growth.reportsLoading;
export const selectGrowthTemplatesLoading = (state) => state.growth.templatesLoading;
export const selectGrowthGeneratingPlan = (state) => state.growth.generatingPlan;
export const selectGrowthError = (state) => state.growth.error;
